'use client';
/* eslint-disable tailwindcss/no-arbitrary-value */
import { ReactNode, useState } from 'react';
import { createPortal } from 'react-dom';
import { BaseTranslations } from '../../baseTranslations';
import { useClient } from '../../hooks/useClient';
import { usePinnedBottomHeight } from '../../hooks/usePinnedBottomHeight';
import { useCmsClientContext } from '../../utils/cmsPageContext';
import { getClientTranslations } from '../../utils/translations/getClientTranslations';
import { ProductDetailsProps } from './ProductDetails';
import { getSectionBuilder } from './SectionBuilders/getSectionBuilder';
import { getNavigationItems, sectionAnchorId } from './utils/sections';

/**
 * Portalled into `<body>` as its last block, so the bar sticks to the viewport for the whole page
 * (not only while this block is on screen) and ends in flow under the site footer. Client-only
 * (portal); `z-[60]` matches the TI provider page footer. The panel's footer needs no such
 * publishing: the modal layer sits above the Sort & Filter trigger.
 */
const PageBottomSticky: React.FC<{ children: ReactNode }> = ({ children }) => {
  const body = useClient(() => document.body);
  // What the bar covers, for the Sort & Filter button and rail to stay clear of. Always shown: it
  // stays at the viewport's bottom edge to the end of the page.
  const [bar, setBar] = useState<HTMLDivElement | null>(null);
  usePinnedBottomHeight(bar, true);

  return body
    ? createPortal(
        <div
          ref={setBar}
          data-component="product-details-page-footer"
          className="sticky bottom-0 z-[60]"
        >
          {children}
        </div>,
        body,
      )
    : null;
};

/** Full-page frame: the vertical's sticky nav above its sections in the content column, and its sticky footer. */
export const ProductDetailsPageMode: React.FC<ProductDetailsProps> = (
  props,
) => {
  const context = useCmsClientContext();
  const staticText = getClientTranslations(BaseTranslations, context);
  const { Navigation, Footer, sections } = getSectionBuilder(props.vertical)(
    props,
    staticText,
  );

  return (
    <div className="flex flex-col gap-6">
      <Navigation {...props} navigationItems={getNavigationItems(sections)} />
      <div className="flex flex-col gap-6 max-w-[--breakpoints-content-narrow-3-lg] w-full lg:mx-auto">
        {sections.map((section) => (
          <div
            key={section.id}
            id={sectionAnchorId(section.id)}
            className="px-spacing/padding/section-x lg:px-0"
          >
            {section.render()}
          </div>
        ))}
      </div>
      {Footer && (
        <PageBottomSticky>
          <Footer {...props} />
        </PageBottomSticky>
      )}
    </div>
  );
};
